import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  Box,
  FlexBox,
  Typography,
  AbsoluteBox,
  InputField,
  Grid,
  MuiBox,
  textSize,
  color
} from '@kis-technologies/kis-ui';
import { Keyboard } from 'components/Keyboard';
import { NumPadAction } from 'components/NumPad';
import { Scrollbar } from 'components/Scrollbar';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import {
  selectors as configSelectors,
  Configuration,
  ConfigInputField,
  LocalizedStrRecord
} from 'services/configuration';
import { validateInput, validateInputType } from 'utils/validation';
import { useStyles } from '../styled';
import { LookupProps } from '../types';

const CustomSearch: React.FC<LookupProps> = (props: LookupProps) => {
  const { onNext, onPrev, loading, lookupOption } = props;
  const { t, i18n } = useTranslation();

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const lookupEnabled = get(lookupOption, 'enabled', false);
  const lookupFields = get(lookupOption, 'lookupOption.fields', []) as ConfigInputField[];
  const lookupHeader = get(lookupOption, `lookupOption.messages.header.${i18n.language}`);
  const lookupSubheader = get(lookupOption, `lookupOption.messages.subheader.${i18n.language}`, '');
  const emailSuggestions: string[] = get(config, `messaging.${i18n.language}.emailSuggestions.buttons`, [
    '@gmail.com',
    '@yahoo.com',
    '@outlook.com'
  ]);

  // SET INITIAL OBJECTS
  const [formData, setFormData] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<Record<string, string>>({});
  const formRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [displayFields, setDisplayFields] = React.useState<ConfigInputField[]>([]);
  const [inputControl, setInputControl] = React.useState('');
  const [apiKey, setAPIKey] = React.useState('');
  const [selectedField, setSelectedField] = React.useState<ConfigInputField | null>(null);
  const [inputIndex, setInputIndex] = React.useState(-1);
  const [validation, setValidation] = React.useState(false);
  const [hideScrollBar, setHideScrollBar] = React.useState(false);

  /** True if at least one field has a value */
  const hasData = Object.values(formData).some((val) => val && val.trim() !== '');

  const classes = useStyles({ inputControl })();

  useEffect(() => {
    if (lookupEnabled && lookupFields && lookupFields.length) {
      setDisplayFields(lookupFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupFields]);

  useEffect(() => {
    setValidation(false);
    const apiParams = displayFields.map((item) => item.apiParam);

    const initialFormRefs: Record<string, HTMLInputElement | null> = {};
    const initialFormData: Record<string, string> = {};

    apiParams.forEach((param) => {
      initialFormRefs[param] = null;
      initialFormData[param] = '';
    });

    formRefs.current = initialFormRefs;
    setError(initialFormData);
    setFormData(initialFormData);

    // If there is only one field to display, make input focused and keyboard displayed
    if (displayFields && displayFields.length === 1) {
      setAPIKey(displayFields[0].apiParam);
      setSelectedField(displayFields[0]);
      setInputIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayFields]);

  useEffect(() => {
    // Check if apiKey is valid, and prevent the effect from running if it's not.
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
      return;
    }

    // Get the actual HTMLInputElement from formRefs.current
    const selectedInput = formRefs.current[apiKey];
    // Only proceed if the selected input element actually exists (is not null)
    if (selectedInput) {
      setTimeout(() => {
        if (inputControl) {
          selectedInput.focus();
          if (selectedInput.type === 'text') {
            selectedInput.setSelectionRange(selectedInput.value.length, selectedInput.value.length, 'forward');
          }
        }

        const elementToScroll = selectedInput.lastElementChild || selectedInput;
        elementToScroll.scrollIntoView({ block: 'center' });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIndex, inputControl, apiKey]);

  useEffect(() => {
    if (error[apiKey]) {
      setError({ ...error, [apiKey]: '' });
      const currIndex = displayFields.findIndex((item) => item.apiParam === apiKey);
      validate(currIndex);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (selectedField) {
      setTimeout(() => {
        handleKeyBoardVisibility(selectedField.keyboard);
      }, 250);
    }
  }, [selectedField]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // Handles the keyboard key click -- changes input accordingly
  const onClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    const currentFieldValue = String(formData[apiKey] || '');

    if (v === 'delete') {
      setFormData((prev) => ({
        ...prev,
        [apiKey]: currentFieldValue.slice(0, currentFieldValue.length - 1)
      }));
    } else if (v === 'clear') {
      setFormData((prev) => ({ ...prev, [apiKey]: '' }));
    } else {
      if (selectedField && selectedField.maxChar) {
        if (selectedField.maxChar > currentFieldValue.length) {
          setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
      }
    }
  };

  const onSuggestionClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    const currentFieldValue = String(formData[apiKey] || '');

    if (selectedField && selectedField.keyboard === 'country') {
      setFormData((prev) => ({ ...prev, [apiKey]: v }));
    } else {
      setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
    }
  };

  // Handle which input ref has the focus
  const handleFocus = (item: ConfigInputField, index: number) => {
    setSelectedField(item);
    setAPIKey(item.apiParam);
    setInputIndex(index);
  };

  const handleControlsHide = () => {
    setTimeout(() => {
      setHideScrollBar(false);
      setInputControl('');
      validateForm();
    }, 0);
  };

  const isValidInput = (item: string, type: string, key: string) => {
    const errorMsg: any = validateInputType(item, type);

    if (errorMsg) {
      setError((prevState) => ({ ...prevState, [key]: errorMsg }));
      setValidation(false);
      return false;
    }
    return true;
  };

  const validate = (currIndex: number) => {
    const currItem = displayFields[currIndex];
    const errorStr = validateInput(currItem, formData);

    if (errorStr) {
      setError((prevState) => ({ ...prevState, [currItem.apiParam]: errorStr }));
      setValidation(false);
      return false;
    } else if (formData[currItem.apiParam] && currItem.inputType) {
      return isValidInput(formData[currItem.apiParam], currItem.inputType, currItem.apiParam);
    }
    return true;
  };

  const validateForm = (canTriggerNextPage?: boolean) => {
    let count = 0;
    displayFields.forEach((_item, index) => {
      const valid = validate(index);
      if (valid) count++;
      if (count === displayFields.length) {
        setValidation(true);
        if (canTriggerNextPage) {
          onNextPageClick(true);
        }
      } else {
        setValidation(false);
      }
    });
  };

  // Handles Next button click on keyboard
  const onNextClick = (index: number) => {
    validate(index);
    const newEl = displayFields[index + 1];
    if (newEl) {
      setAPIKey(newEl.apiParam);
      setSelectedField(newEl);
      setInputIndex(index + 1);
    } else {
      handleControlsHide();
      if (displayFields.length === 1) {
        validateForm(true);
      }
    }
  };

  const onBackClick = (index: number) => {
    validate(index);
    const newEl = displayFields[index - 1];
    if (newEl) {
      setAPIKey(newEl.apiParam);
      setSelectedField(newEl);
      setInputIndex(index - 1);
    } else {
      handleControlsHide();
    }
  };

  const handleKeyBoardVisibility = (type = 'alphaNumeric') => {
    if (type === 'numeric' || type === 'date') {
      setInputControl('numpad');
    } else {
      setInputControl('keyboard');
    }
    setHideScrollBar(true);
  };

  const onNextPageClick = (skipValidation?: boolean) => {
    if (validation || skipValidation) {
      onNext(getPayload());
    } else {
      validateForm();
      const errStr = Object.values(error).find((item) => item.length);
      if (errStr) {
        const idx = Object.values(error).findIndex((item) => item === errStr);
        handleFocus(displayFields[idx], idx);
      }
    }
  };

  const getLocalizedText = (textObj: LocalizedStrRecord = {}, apiParam?: string): string => {
    return get(textObj, `${i18n.language}`) || t(`inputFieldLabel.${apiParam}`);
  };

  /**
   * Returns the component to use as the label for an input field.
   *
   * There is an error when passing `label` to InputField if it is a child of InputMask.
   * The workaround is to render the label separately, which requires additional
   * styles to match what is applied to the `label` of other InputField(s).
   */
  const getLabelText = (configInputField: ConfigInputField) => {
    const { required, label, apiParam, inputMask } = configInputField;

    const typographyStyles = inputMask ? { padding: '0 24px', margin: '0 0 10px' } : {};

    return (
      <Typography fontSize={24} fontWeight={600} sx={typographyStyles}>
        {required ? <span style={{ color: 'red' }}>* </span> : ''}
        {getLocalizedText(label, apiParam)}
      </Typography>
    );
  };

  const getNextText = (idx: number) => {
    let nextLabel = '';
    if (displayFields.length === 1) {
      nextLabel = t('commonText.continue');
    } else {
      if (idx === displayFields.length - 1) {
        nextLabel = t('commonText.done');
      } else {
        nextLabel = t('commonText.next');
      }
    }

    return nextLabel;
  };

  const getPayload = () => {
    const payload: { [k: string]: any } = {};
    lookupFields.forEach((item) => {
      if (formData[item.apiParam]) {
        payload[item.apiParam] = formData[item.apiParam].trim();
      }
    });

    return payload;
  };

  const getSuggestions = () => {
    let result;
    if (selectedField && selectedField.keyboard === 'email') {
      result = emailSuggestions;
    }

    return result;
  };

  if (loading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      <Grid container alignContent="space-between" className={classes.customSearchOuterContainer}>
        <Grid container item alignContent="space-between" className={classes.customSearchInnerContainer}>
          {displayFields && displayFields.length ? (
            <>
              <Box width="100%" flexOverride="none" margin="0 0 6">
                <Typography fontSize={64} fontWeight={600} align="center">
                  {lookupHeader}
                </Typography>
                <Typography fontSize={30} align="center">
                  {lookupSubheader}
                </Typography>
              </Box>
              <MuiBox className={classes.customSearchInputsBox}>
                <Scrollbar hideScrollBar={hideScrollBar} refreshModel={displayFields}>
                  <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                    <Grid container alignContent="flex-start" className={classes.customSearchInputsContainer}>
                      {displayFields &&
                        displayFields.map((configInput, idx) => (
                          <Grid
                            key={idx}
                            item
                            xs={configInput.halfWidth ? 6 : 12}
                            sx={
                              configInput.inputType === 'header' ? { padding: '0px 20px 0' } : { padding: '10px 20px' }
                            }
                          >
                            {configInput.inputMask ? (
                              <>
                                {getLabelText(configInput)}
                                <InputMask
                                  mask={configInput.inputMask}
                                  value={formData[configInput.apiParam] || ''}
                                  onFocus={(_e) => handleFocus(configInput, idx)}
                                  name={configInput.apiParam}
                                  maskPlaceholder={getLocalizedText(
                                    configInput.placeholder ? configInput.placeholder : configInput.label,
                                    configInput.apiParam
                                  )}
                                  onClick={(_e) => {
                                    setInputIndex(idx);
                                    handleKeyBoardVisibility(configInput.keyboard);
                                  }}
                                >
                                  <InputField
                                    size="large"
                                    fullWidth
                                    type="text"
                                    inputProps={{ maxLength: 8 }}
                                    placeholder={getLocalizedText(
                                      configInput.placeholder ? configInput.placeholder : configInput.label,
                                      configInput.apiParam
                                    )}
                                    error={Boolean(error[configInput.apiParam])}
                                    sx={{ height: '80px' }}
                                    className={classes.customSearchInputField}
                                    inputRef={(el: HTMLInputElement | null) => {
                                      formRefs.current[configInput.apiParam] = el;
                                    }}
                                  />
                                </InputMask>
                              </>
                            ) : (
                              <InputField
                                size="large"
                                fullWidth
                                type="text"
                                name={configInput.apiParam}
                                label={getLabelText(configInput)}
                                placeholder={getLocalizedText(
                                  configInput.placeholder ? configInput.placeholder : configInput.label,
                                  configInput.apiParam
                                )}
                                value={formData[configInput.apiParam] || ''}
                                error={Boolean(error[configInput.apiParam])}
                                onFocus={(_e) => handleFocus(configInput, idx)}
                                onClick={(_e) => {
                                  setInputIndex(idx);
                                  handleKeyBoardVisibility(configInput.keyboard);
                                }}
                                sx={{ height: '80px' }}
                                className={classes.customSearchInputField}
                                inputRef={(el: HTMLInputElement | null) => {
                                  formRefs.current[configInput.apiParam] = el;
                                }}
                              />
                            )}
                            {error[configInput.apiParam] && error[configInput.apiParam].length && (
                              <Typography
                                color={color.RED}
                                fontSize={textSize.kiosk.SMALL}
                                sx={{ padding: '0 0 0 58px' }}
                              >
                                {error[configInput.apiParam]}
                              </Typography>
                            )}
                          </Grid>
                        ))}
                    </Grid>
                  </FlexBox>
                </Scrollbar>
              </MuiBox>
            </>
          ) : (
            <FlexBox sx={{ height: '650px' }} justify="center" align="center">
              <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
            </FlexBox>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      {!inputControl && (
        <Footer
          onPrevClick={onPrev}
          onNextClick={onNextPageClick}
          isNextDisabled={!hasData || !validation}
          nextText={t('commonText.continue')}
        />
      )}

      {/* Keyboard */}
      {inputControl === 'keyboard' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
          <Grid>
            <Keyboard
              language={i18n.language}
              onClick={onClick}
              onNext={() => onNextClick(inputIndex)}
              onPrev={() => onBackClick(inputIndex)}
              nextText={getNextText(inputIndex)}
              prevText={inputIndex ? t('commonText.previous') : t('commonText.back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              suggestions={getSuggestions()}
              onSuggestionClick={onSuggestionClick}
            />
          </Grid>
        </AbsoluteBox>
      )}

      {inputControl === 'numpad' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <Grid>
            <NumPadAction
              onClick={onClick}
              onConfirm={() => onNextClick(inputIndex)}
              onBack={() => onBackClick(inputIndex)}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              layout="row"
              confirmConfig={{
                label: getNextText(inputIndex),
                backLabel: inputIndex ? t('commonText.previous') : t('commonText.back'),
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          </Grid>
        </AbsoluteBox>
      )}
    </>
  );
};

export default CustomSearch;
